import type { SeenMedplumClient } from '#src/client.ts'
import { carePlanPageQuestionnaireUrls } from '#src/models/care-plans/demographics.ts'
import { InitialCarePlan } from '#src/models/care-plans/InitialCarePlan.ts'
import { ExtendedQuestionnaireResponse } from '#src/models/questionnaireResponse.ts'
import { resolveId } from '#src/references.ts'
import { parseReferenceString } from '#src/utils.ts'

export type ResourceManifestEntryKind = 'questionnaire-response'

export interface ResourceManifestEntry {
  // Full FHIR reference of a resource the open resource pins.
  reference: string
  kind: ResourceManifestEntryKind
  // Canonical questionnaire URL, for a questionnaire response.
  key: string
}

/* The exact resources the client's open resource is built from, resolved
 * server-side so neither the model nor the browser decides which resource a
 * tool edits. Carries identity only: content is re-read at execution, so an
 * entry cannot go stale into a write. */
export interface ResourceManifest {
  // The resource open in the client. Every entry is resolved from it.
  subject: string
  // False once the plan is signed. Tools must refuse writes.
  editable: boolean
  entries: ResourceManifestEntry[]
  /* Canonical urls of the questionnaires the open resource's page renders —
   * see `carePlanPageQuestionnaireUrls`. Only these may be read for proposing
   * or proposed for: a suggestion for any other would stage where the
   * clinician cannot see it. A plan can pin a response it does not render, so
   * this is not `entries`' keys. */
  questionnaireUrls: string[]
  /* Full references of every resource that is functionally part of the open
   * resource, beyond what `entries` carries: all pinned questionnaire
   * responses — including one without a canonical questionnaire url, which
   * `entries` has to drop — and the plan's own signed snapshot compositions.
   * The agent excludes `subject` plus this set from the citable evidence set,
   * because citing any of them would be a proposal citing the document it is
   * being staged into. */
  constituents: string[]
}

export function findManifestEntry(
  manifest: ResourceManifest | undefined,
  kind: ResourceManifestEntryKind,
  key: string
): ResourceManifestEntry | undefined {
  return manifest?.entries.find(
    (entry) => entry.kind === kind && entry.key === key
  )
}

export class ResourceManifestParticipantMismatch extends Error {
  constructor(subject: string, patientId: string) {
    super(`${subject} does not belong to Patient/${patientId}`)
    this.name = 'ResourceManifestParticipantMismatch'
  }
}

/* Resolves the manifest for a care plan the client has open. Returns undefined
 * when there is nothing to pin — a non-CarePlan reference, or a plan that
 * carries no supporting responses — so callers fall back to their existing
 * behaviour rather than losing the tool entirely.
 *
 * Throws ResourceManifestParticipantMismatch when the reference names a plan
 * belonging to someone else. That is the one failure that must not degrade
 * quietly: it means the client asked to act on another participant's record. */
export async function resolveCarePlanManifest(
  medplum: SeenMedplumClient,
  resourceRef: string | undefined,
  patientId: string
): Promise<ResourceManifest | undefined> {
  if (!resourceRef || parseReferenceString(resourceRef)?.type !== 'CarePlan') {
    return undefined
  }

  const carePlan = await medplum.readResourceClass(InitialCarePlan, resourceRef)
  /* Compared by id, not by reference string: a subject stored in any other
   * valid form would otherwise read as a different participant and 403 the
   * whole chat turn. */
  if (resolveId(carePlan.patientSubject) !== patientId) {
    throw new ResourceManifestParticipantMismatch(resourceRef, patientId)
  }

  const references = carePlan.supportingQuestionnaireResponses
    .map((reference) => reference.reference)
    .filter((reference): reference is string => Boolean(reference))

  /* Read each reference rather than one `_id` search: a plan pins at most a
   * handful, and a read resolves the exact resource the plan names, which is
   * the whole point of the manifest. */
  const responses = await Promise.all(
    references.map((reference) =>
      medplum.readResourceClass(ExtendedQuestionnaireResponse, reference)
    )
  )

  return {
    subject: resourceRef,
    editable: carePlan.isEditable,
    questionnaireUrls: carePlanPageQuestionnaireUrls(carePlan),
    constituents: [
      ...references,
      ...carePlan.signedSnapshots
        .map((reference) => reference.reference)
        .filter((reference): reference is string => Boolean(reference)),
    ],
    entries: responses.flatMap((response, index) => {
      const key = response.questionnaireUrl
      const reference = references[index]
      return key && reference
        ? [{ reference, kind: 'questionnaire-response' as const, key }]
        : []
    }),
  }
}
